import React, { useState } from 'react';
import { Instagram, MessageCircle, Send, Check } from 'lucide-react';
import { BRAND } from '../mock';
import { createLead } from '../lib/api';

export default function Contact() {
  const [form, setForm] = useState({ name: '', phone: '', message: '', hp: '' });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    if (form.hp) return; // honeypot triggered — silently ignore
    // Save to backend (fire-and-forget) then open WhatsApp
    createLead({ name: form.name, phone: form.phone, message: form.message, hp: form.hp }).catch(() => {});

    // Also open WhatsApp conversation for immediate contact
    const wa = `https://wa.me/${BRAND.whatsappIntl}?text=${encodeURIComponent(
      `Merhaba Leila Tesettür,\n\nAd Soyad: ${form.name}\nTelefon: ${form.phone}${form.message ? `\nMesaj: ${form.message}` : ''}`
    )}`;
    window.open(wa, '_blank');

    setSent(true);
    setForm({ name: '', phone: '', message: '' });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="iletisim" className="bg-[#efe6d8] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="text-[11px] tracking-[0.3em] uppercase text-stone-500">İletişim</p>
          <h2 className="mt-3 font-serif text-4xl lg:text-6xl leading-[1.05] text-stone-900">
            Sizinle <em className="italic font-light">tanışmak</em> isteriz.
          </h2>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-stone-700">
            Bilgilerinizi bırakın, en kısa sürede WhatsApp üzerinden size ulaşalım. Yeni
            koleksiyon, kampanya ve özel parçalar hakkında bilgi alın.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 p-5 bg-[#f7f2ec] border border-stone-200 hover:border-stone-900 transition-colors group"
            >
              <div className="h-11 w-11 flex items-center justify-center bg-stone-900 text-[#f7f2ec] rounded-full group-hover:bg-stone-800">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-stone-500">WhatsApp</div>
                <div className="mt-0.5 font-serif text-lg text-stone-900">+90 535 064 00 41</div>
              </div>
            </a>

            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 p-5 bg-[#f7f2ec] border border-stone-200 hover:border-stone-900 transition-colors group"
            >
              <div className="h-11 w-11 flex items-center justify-center bg-stone-900 text-[#f7f2ec] rounded-full group-hover:bg-stone-800">
                <Instagram className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Instagram</div>
                <div className="mt-0.5 font-serif text-lg text-stone-900">@{BRAND.instagram}</div>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="bg-[#f7f2ec] border border-stone-200 p-8 lg:p-10 space-y-6">
            {/* Honeypot: hidden field for bots */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.hp}
              onChange={(e) => setForm({ ...form, hp: e.target.value })}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Ad Soyad</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full bg-transparent border-b border-stone-300 focus:border-stone-900 outline-none py-3 text-stone-900 placeholder:text-stone-400 transition-colors"
                placeholder="Adınız"
              />
            </div>

            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">WhatsApp Numarası</label>
              <input
                type="tel"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-2 w-full bg-transparent border-b border-stone-300 focus:border-stone-900 outline-none py-3 text-stone-900 placeholder:text-stone-400 transition-colors"
                placeholder="05XX XXX XX XX"
              />
            </div>

            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Mesaj (opsiyonel)</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 w-full bg-transparent border-b border-stone-300 focus:border-stone-900 outline-none py-3 text-stone-900 placeholder:text-stone-400 resize-none transition-colors"
                placeholder="Bize iletmek istedikleriniz..."
              />
            </div>

            <button
              type="submit"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-stone-900 text-[#f7f2ec] text-[11px] tracking-[0.24em] uppercase hover:bg-stone-800 transition-colors"
            >
              {sent ? (
                <>
                  <Check className="h-4 w-4" /> Gönderildi
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> Bilgilerimi Gönder
                </>
              )}
            </button>

            <p className="text-[12px] text-stone-500">
              Bilgileriniz yalnızca size ulaşmak için kullanılır.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
